"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useCallback } from "react";
import { usePathname } from "next/navigation";
import appLinks from '@/../config/appLinks.json'; // Import app links

// Define the type for navigation items
type NavItem = {
  id: string;
  href: string;
  label: string;
  disabled?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { id: 'why', href: "/why-meetminutes", label: "Why MeetMinutes?" },
  { id: 'about', href: "/about-us", label: "About" },
  { id: 'pricing', href: "/pricing", label: "Pricing" },
  { id: 'roi', href: "/roi-calculator", label: "ROI Calculator" },
  { id: 'blog', href: "/blog", label: "Blog" },
  { id: 'contact', href: "/contact", label: "Contact" },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (mobileMenuOpen && !target.closest('.mobile-menu')) {
        closeMobileMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [mobileMenuOpen, closeMobileMenu]);

  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu();
  }, [pathname, closeMobileMenu]);

  return (
    <div className="sticky top-0 left-0 right-0 z-40">
      {/* Navigation Bar */}
      <div className="bg-black/80 backdrop-blur-sm py-1 md:py-4 shadow-md transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center">
            {/* Logo */}
            <Link href="/" className="flex-shrink-0 mr-auto flex items-center group">
              <Image
                src="/images/logo/logo_with_name.svg"
                alt="MeetMinutes Logo"
                width={120}
                height={40}
                className="h-10 w-auto transition-transform group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation - All items right aligned */}
            <div className="hidden md:flex items-center space-x-6">
              {NAV_ITEMS.map((item) => (
                item.disabled ? (
                  <span
                    key={item.id}
                    className="text-gray-500 cursor-not-allowed"
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    key={item.id}
                    href={item.href}
                    className={`relative text-gray-300 hover:text-white transition-colors ${
                      pathname === item.href 
                        ? 'text-white font-medium after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-500 after:rounded-full' 
                        : 'after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-blue-500 after:rounded-full hover:after:w-full after:transition-all after:duration-300'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              ))}
              
              {/* Desktop Login Link - Use config */}
              <Link
                href={appLinks.loginLink}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm py-2 px-6 rounded-full inline-flex items-center justify-center transition-colors ml-2"
              >
                {appLinks.loginButtonText}
              </Link>
            </div>

            {/* Mobile menu button - reduced size */}
            <button
              className="md:hidden text-gray-300 focus:outline-none text-lg p-1 hover:bg-gray-800 rounded-lg transition-colors"
              onClick={toggleMobileMenu}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`mobile-menu md:hidden fixed inset-0 z-50 bg-black transition-all duration-300 ${
            mobileMenuOpen 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 -translate-y-full pointer-events-none'
          }`}
        >
          <div className="container mx-auto px-4 bg-black">
            <div className="flex justify-between items-center py-1">
              <Link href="/" className="flex items-center" onClick={closeMobileMenu}>
                <Image
                  src="/images/logo/logo_with_name.svg"
                  alt="MeetMinutes Logo"
                  width={120}
                  height={40}
                  className="h-10 w-auto"
                />
              </Link>
              <button
                className="text-gray-300 focus:outline-none text-lg p-1 hover:bg-gray-800 rounded-lg transition-colors"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>
            <nav className="flex flex-col items-center space-y-4 mt-4">
              {NAV_ITEMS.map((item) => (
                item.disabled ? (
                  <span
                    key={item.id}
                    className="text-gray-500 cursor-not-allowed text-base"
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    key={item.id}
                    href={item.href}
                    className={`text-gray-300 hover:text-white transition-all duration-300 text-base ${
                      pathname === item.href 
                        ? 'text-white font-medium scale-110' 
                        : 'hover:scale-105'
                    }`}
                    onClick={closeMobileMenu}
                  >
                    {item.label}
                  </Link>
                )
              ))}
              {/* Mobile Login Link - Use config */}
              <Link
                href={appLinks.loginLink}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base py-1.5 px-6 rounded-full inline-flex items-center justify-center transition-colors mt-4 mb-6"
                onClick={closeMobileMenu}
              >
                {appLinks.loginButtonText}
              </Link>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
